import { useCallback, useEffect, useRef, useState } from 'react'
import { auth } from '../firebase'
import {
    createTypingPublisher,
    listenPreview,
    listenTyping,
} from '../utils/typingStatus'

/**
 * Typing indicator, clinician side, over /typingStatus and /typingPreview
 * (utils/typingStatus.js): the nodes and rules the registration app's chat
 * already uses, keyed by user ID.
 *
 * The clinician's entry is STATUS ONLY, a heartbeat number. No text is ever
 * written for the clinician: the patient must not be able to see what is being
 * typed. That is enforced here, at the write.
 */

/** Writer. Returns setTyping(draft) and clearTyping(). */
export const useTypingIndicator = (chatID) => {
    const uid = auth.currentUser?.uid || null
    const publisher = useRef(null)

    useEffect(() => {
        if (!chatID || !uid) return undefined
        const pub = createTypingPublisher(chatID, uid)
        publisher.current = pub
        return () => {
            pub.stop()
            if (publisher.current === pub) publisher.current = null
        }
    }, [chatID, uid])

    const clearTyping = useCallback(() => {
        if (publisher.current) publisher.current.stop()
    }, [])

    const setTyping = useCallback((text) => {
        if (!publisher.current) return
        if (!String(text || '').trim()) {
            publisher.current.stop()
            return
        }
        publisher.current.ping()
    }, [])

    return { setTyping, clearTyping }
}

/**
 * Reader: the patient's heartbeat and draft preview.
 * Returns null when the patient is not typing, or { isTyping, text } when they are.
 *
 * ChatList stays mounted across chat switches, so every piece of state is
 * tagged with the subscription (chatID/patientUid) it came from and anything
 * from a previous subscription is ignored on read. Resetting state inside an
 * effect instead would still paint one frame of the previous patient's draft
 * under the new patient's name.
 */
export const useTypingListener = (chatID, patientUid) => {
    const sub = `${chatID}/${patientUid}`
    const [typingState, setTypingState] = useState({ sub: null, on: false })
    const [previewState, setPreviewState] = useState({ sub: null, text: '' })

    useEffect(
        () => listenTyping(chatID, patientUid, (on) => setTypingState({ sub, on })),
        [sub, chatID, patientUid]
    )
    useEffect(
        () => listenPreview(chatID, patientUid, (text) => setPreviewState({ sub, text })),
        [sub, chatID, patientUid]
    )

    if (typingState.sub !== sub || !typingState.on) return null
    return { isTyping: true, text: previewState.sub === sub ? previewState.text : '' }
}

export default useTypingIndicator
